import { useMemo } from "react";

import { DashboardCommsToSendSection } from "./DashboardCommsToSendSection";
import { DashboardSchedulePanelViewToggle } from "./DashboardSchedulePanelViewToggle";
import { DashboardTaskCategorySection } from "./DashboardTaskCategorySection";
import { DashboardTaskDayNav } from "./DashboardTaskDayNav";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";

import { useCreateAddDashboardTask } from "@/hooks/createAdds/useCreateAddDashboardTask";
import { dashboardScheduleWeek } from "@/lib/dashboardScheduleWeek";
import {
  CommsToSendCandidacy,
  DashboardScheduleTaskCategory,
  DashboardSchedulePanelView,
  DashboardTask,
} from "@/types";

export const TasksContainerMini = ({
  tasks,
  commsToSend,
  day,
  onDayChange,
  panelView,
  onPanelViewChange,
  showTaskOwnerAvatars = false,
  newTaskAssigneeUserId,
}: {
  tasks: DashboardTask[];
  commsToSend: CommsToSendCandidacy[];
  day: Date;
  onDayChange: (day: Date) => void;
  panelView: DashboardSchedulePanelView;
  onPanelViewChange: (view: DashboardSchedulePanelView) => void;
  showTaskOwnerAvatars?: boolean;
  newTaskAssigneeUserId: number | null;
}) => {
  /* Database CRUD Operations */
  // Create
  const { mutate: createTask } = useCreateAddDashboardTask();

  /* React Hooks */
  const dayTasks = useMemo(
    () => dashboardScheduleWeek.tasksForDay(tasks, day),
    [tasks, day],
  );

  /* Handlers */
  // Quick-add a general task on the viewed day, owned by the viewed staffer.
  const handleAddTask = (title: string) => {
    if (newTaskAssigneeUserId == null) return;
    createTask({
      title,
      dueDate: day.toISOString(),
      assignees: [newTaskAssigneeUserId],
    });
  };

  /* Render */
  const render = () => (
    <Column className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
      <Row className="h-12 shrink-0 items-center border-b border-slate-200 bg-slate-50 px-3">
        <Text className="text-sm font-medium text-slate-500">{"Tasks"}</Text>
      </Row>
      <Column className="min-h-0 flex-1 gap-4 overflow-y-auto px-3 pb-4 pt-3">
        <DashboardTaskDayNav day={day} onDayChange={onDayChange} />
        {dashboardScheduleWeek.listCategoriesForDay(day).map((category) => (
          <DashboardTaskCategorySection
            key={category}
            category={category}
            day={day}
            dayTasks={dayTasks}
            onAddTask={
              category === DashboardScheduleTaskCategory.GENERAL
                ? handleAddTask
                : undefined
            }
            showOwnerAvatar={showTaskOwnerAvatars}
          />
        ))}
        <DashboardCommsToSendSection candidacies={commsToSend} />
      </Column>
      <Row className="shrink-0 items-center justify-end px-3 py-2.5">
        <DashboardSchedulePanelViewToggle
          value={panelView}
          onChange={onPanelViewChange}
        />
      </Row>
    </Column>
  );

  return render();
};
